<template>
    <div class="portal-wrapper">
      <div class="advance-container">
        
        <!-- ─── HEADER SECTION ─── -->
        <header class="advance-header">
          <h1 class="main-title">Enter Amount</h1>
          <p class="sub-title">
            Specify the amount you wish to advance for your rental property.
          </p>
        </header>
  
        <!-- ─── INTERACTIVE CARD ─── -->
        <div class="amount-card">
          
          <!-- Interactive Input Field Container -->
          <div class="input-row">
            <span class="currency-symbol">₦</span>
            <input 
              type="text" 
              class="amount-text-input"
              id="amount"
              name="amount" 
              :value="formattedAmount" 
              @keydown="restrictToNumbersOnly"
              @input="handleTextInput"
              placeholder="0"
            />
          </div>
  
          <!-- Available Limit Pill Badge -->
          <div class="limit-badge">
            AVAILABLE LIMIT: ₦{{ formatNumber(maxLimit) }}
          </div>
  
          <!-- Range Slider Module Layout -->
          <div class="slider-wrapper">
            <input 
              type="range" 
              class="custom-range-slider"
              :min="minLimit" 
              :max="maxLimit" 
              :step="5000"
              v-model.number="amount"
              :style="sliderProgressStyle"
            />
            
            <!-- Explicit Tick Scale Markers Grid -->
            <div class="slider-labels">
              <span class="tick-mark">₦50k</span>
              <span class="tick-mark">₦{{ Number(halfAmount).toLocaleString() }}</span>
              <span class="tick-mark">₦{{ Number(maxLimit).toLocaleString() }}</span>
            </div>
          </div>
  
        </div>
  
        <!-- ─── SYSTEM INSIGHT TIP WIDGET ─── -->
        <div class="insight-notice-card">
          <div class="bulb-icon-frame">
            <!-- Inline Lightweight Vector Micro Lightbulb -->
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
              <path d="M15 14c.2-1 .7-1.7 1.5-2.5 1-.9 1.5-2.2 1.5-3.5A5 5 0 0 0 8 8c0 1.3.5 2.6 1.5 3.5.8.8 1.3 1.5 1.5 2.5" />
              <line x1="9" y1="18" x2="15" y2="18" />
              <line x1="10" y1="22" x2="14" y2="22" />
            </svg>
          </div>
          <p class="insight-notice-text">
            Repayments are automatically deducted from your rent collection for hassle-free management.
          </p>
        </div>
        
        <div class="button-holder">
          <button class="btn main" @click="routeTo()">Continue</button>
          <button class="btn cancel" @click="goBack()">Cancel</button>
        </div>
  
      </div>
    </div>
  </template>
  
  <script>
  import { Service } from "../../store/service";
  const Api = new Service();
  export default {
    name: 'AdvanceAmount',
    data() {
      return {
        amount: 0,    // Reactive source of truth
        minLimit: 50000,   // ₦50k min gate
        maxLimit: "",
        halfAmount: 0
      };
    },
    computed: {
      // Computed formatting for raw reactive integers: e.g. 300000 -> "300,000"
      formattedAmount() {
        if (!this.amount && this.amount !== 0) return '';
        return this.formatNumber(this.amount);
      },
      // Dynamic Linear Gradient tracker for custom cross-browser slider tracking
      sliderProgressStyle() {
        const percentage = ((this.amount - this.minLimit) / (this.maxLimit - this.minLimit)) * 100;
        return {
          background: `linear-gradient(to right, #2563eb 0%, #2563eb ${percentage}%, #e2e8f0 ${percentage}%, #e2e8f0 100%)`
        };
      }
    },
    mounted() {
        this.dataForAdvanceRent();
    },
    methods: {
      // Utility for regex thousands separation splits
      formatNumber(val) {
        return val.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ',');
      },
      restrictToNumbersOnly(e) {
        const allowedKeys = [
            'Backspace', 'Delete', 'Tab', 'Escape', 'Enter', 
            'ArrowLeft', 'ArrowRight', 'ArrowUp', 'ArrowDown', 'Home', 'End'
        ];

        // Allow functional system shortcuts (e.g., Ctrl+A, Ctrl+C, Ctrl+V)
        if (allowedKeys.includes(e.key) || (e.ctrlKey === true || e.metaKey === true)) {
            return;
        }

        // Allow only one single decimal point parameter
        if (e.key === '.' && !e.target.value.includes('.')) {
            return;
        }

        // If the character is not a digit between 0-9, kill the keyboard transmission event
        if (!/^[0-9]$/.test(e.key)) {
            e.preventDefault();
        }
      },
      // Handles sanitized input changes cleanly across two-way pipelines
      handleTextInput(e) {
        const rawValue = e.target.value.replace(/,/g, '');
        
        // Prevent alpha text crashes
        if (rawValue === '') {
          this.amount = 0;
          return;
        }
  
        const parsedNumeric = parseInt(rawValue, 10);
        if (isNaN(parsedNumeric)) return;
  
        // Restrict upper boundary overflow updates on text entry hooks
        if (parsedNumeric > this.maxLimit) {
          this.amount = this.maxLimit;
          this.$forceUpdate(); // Keep structural synchronicity in DOM loops
        } else {
          this.amount = parsedNumeric;
        }
      },
      async dataForAdvanceRent() {
        try {
          await Api.getRequest(`fetch-data-for-advanced-rent`).then(res => {
            console.log("request data", res)
            if (res.data.success) {
                this.maxLimit = res.data.amount;
                const xx = this.maxLimit;
                this.halfAmount = (xx / 2);
            }
          }).catch(err => {
            console.log("Error:", err);
          })
        }  finally {

        }
      },
        goBack() {
            this.$router.go(-1)
        },
        routeTo() {
            sessionStorage.setItem('advanceAmount', JSON.stringify(this.amount));
            this.$router.push("advance-rent-summary");
        },
    }
  };
  </script>
  
  <style scoped>
  /* ─── SYSTEM CSS VARIABLES ─── */
  .portal-wrapper {
    --font-stack: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Helvetica, Arial, sans-serif;
    --bg-color: #f8fafc;
    --text-main: #0f172a;
    --text-muted: #64748b;
    --accent-blue: #2563eb;
    --accent-light-blue: #eff6ff;
    --notice-bg: #fef3c7;
    --notice-text: #78350f;
    
    font-family: var(--font-stack);
    background-color: var(--bg-color);
    min-height: 100vh;
    display: flex;
    justify-content: center;
    align-items: center;
    padding: 16px;
    box-sizing: border-box;
  }
  
  .advance-container {
    width: 100%;
    max-width: 520px;
    display: flex;
    flex-direction: column;
    gap: 24px;
    border-radius: 28px;
    padding: 2.2rem 1rem;
    box-shadow: 0 0 0 1px rgba(0, 0, 0, 0.05), 0 4px 12px rgba(0, 0, 0, 0.05), 0 12px 24px rgba(0, 0, 0, 0.05);
  }
  
  /* ─── HEADER TYPOGRAPHY ─── */
  .advance-header {
    display: flex;
    flex-direction: column;
    gap: 8px;
  }
  
  .main-title {
    font-size: 1.85rem;
    font-weight: 700;
    color: var(--text-main);
    margin: 0;
  }
  
  .sub-title {
    font-size: 1rem;
    color: var(--text-muted);
    line-height: 1.5;
    margin: 0;
  }
  
  /* ─── CARD LAYOUT FRAME ─── */
  .amount-card {
    background-color: #ffffff;
    border-radius: 12px;
    padding: 32px 24px;
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.02), 0 1px 3px rgba(0, 0, 0, 0.01);
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 20px;
    border: 1px solid rgba(226, 232, 240, 0.6);
  }
  
  /* ─── NUMERIC INPUT STACK ─── */
  .input-row {
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 16px;
    width: 100%;
  }
  
  .currency-symbol {
    font-size: 2.2rem;
    font-weight: 700;
    color: var(--accent-blue);
    user-select: none;
  }
  
  .amount-text-input {
    font-size: 2.85rem;
    font-weight: 700;
    color: var(--text-main);
    border: none;
    border-bottom: 1px solid #E6E9EC;
    outline: none;
    width: 70%;
    padding: 0;
    margin: 0;
    letter-spacing: -0.5px;
    
  }
  
  /* ─── LIMIT BADGE CHIP ─── */
  .limit-badge {
    background-color: var(--accent-light-blue);
    color: #1e40af;
    font-size: 0.72rem;
    font-weight: 700;
    padding: 6px 14px;
    border-radius: 20px;
    letter-spacing: 0.5px;
    text-transform: uppercase;
  }
  
  /* ─── SLIDER CONTAINER & CONTROL MECHANICS ─── */
  .slider-wrapper {
    width: 100%;
    margin-top: 12px;
    display: flex;
    flex-direction: column;
    gap: 12px;
  }
  
  /* Cross-Browser Custom Input Range Normalization reset */
  .custom-range-slider {
    -webkit-appearance: none;
    appearance: none;
    width: 100%;
    height: 6px;
    border-radius: 4px;
    outline: none;
    cursor: pointer;
  }
  
  /* Webkit / Blink Thumb Specific Custom Styles */
  .custom-range-slider::-webkit-slider-thumb {
    -webkit-appearance: none;
    appearance: none;
    width: 22px;
    height: 22px;
    border-radius: 50%;
    background: var(--accent-blue);
    border: 4px solid #ffffff;
    box-shadow: 0 2px 6px rgba(37, 99, 235, 0.4);
    transition: transform 0.1s ease;
  }
  
  .custom-range-slider::-webkit-slider-thumb:active {
    transform: scale(1.15);
  }
  
  /* Firefox Thumb Specific Custom Styles */
  .custom-range-slider::-moz-range-thumb {
    width: 14px;
    height: 14px;
    border-radius: 50%;
    background: var(--accent-blue);
    border: 4px solid #ffffff;
    box-shadow: 0 2px 6px rgba(37, 99, 235, 0.4);
  }
  
  .slider-labels {
    display: flex;
    justify-content: space-between;
    width: 100%;
  }
  
  .tick-mark {
    font-size: 0.85rem;
    font-weight: 500;
    color: var(--text-muted);
  }
  
  /* ─── BOTTOM ACCENT INSIGHT CARD ─── */
  .insight-notice-card {
    background-color: var(--notice-bg);
    border-radius: 12px;
    padding: 16px 20px;
    display: flex;
    gap: 12px;
    align-items: flex-start;
  }
  
  .bulb-icon-frame {
    color: #9a3412;
    width: 22px;
    height: 22px;
    flex-shrink: 0;
    display: flex;
    align-items: center;
    justify-content: center;
  }
  
  .insight-notice-text {
    font-size: 0.92rem;
    color: var(--notice-text);
    line-height: 1.45;
    font-weight: 500;
    margin: 0;
  }
  
  /* ─── RESPONSIVE BREAKPOINT SYSTEM ─── */
  @media (max-width: 480px) {
    .main-title {
      font-size: 1.55rem;
    }
    
    .sub-title {
      font-size: 0.9rem;
    }
    
    .amount-card {
      padding: 24px 16px;
    }
    
    .amount-text-input {
      font-size: 2.25rem;
    }
    
    .currency-symbol {
      font-size: 1.85rem;
    }
  }

    .button-holder {
        margin-top: 30px;
        margin-bottom: 20px;
        width: 100%;
        button {
        width: 100%;
        height: 50px;
        font-family: Lato;
        box-shadow: none;
        outline: 0;
        }
        .main {
        background-color: #0033EA;
        color: #ffffff;
        margin-bottom: 15px;
        }
        .cancel {
        background-color: #E6F0FA;
        color: #0033EA;
        }
    }
</style>
  